import OriginTracer from '@atlassiansox/origin-tracing';
import sortBy from 'lodash/sortBy';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as uuid } from 'uuid';

import { DEFAULT_SLACK_SERVICE_URL } from './constants';
import popupWindowParams from './popupWindowParams';
import type {
	FetchSlackUsersResult,
	GetSlackChannelsResult,
	GetSlackTeamsResult,
	IsSlackEnabledResult,
	ShareToSlackRequest,
	ShareToSlackResult,
	SlackChannel,
	SlackChannelsResponse,
	SlackTeam,
	SlackTeamsServiceResponse,
	SlackUser,
	SlackUsersResponse,
} from './share-to-slack/common/types';
import { isAcceptableFailure } from './share-to-slack/utils';
import type { SlackService } from './SlackService';
import type { AtlassianSlackProduct } from './types';
import {
	failUfoExperience,
	startUfoExperience,
	succeedUfoExperience,
	UfoExperiences,
} from './ufo-experiences';
import { buildQueryString, fetchItemsWithCursor, httpRetryPolicy, retryFetch } from './util';

const POPUP_WIDTH = 500;
const POPUP_HEIGHT = 900;

const USER_FETCH_LIMIT = 1000;

const retryOptions = {
	maxAttempts: 3,
	backoffTimeMs: 200,
	rateLimitBackoffTimeMs: 1000,
};

type HttpError = {
	errorMessage?: string;
	status: number;
};

type PaginatedError = HttpError & {
	page: number;
};

export type SlackServiceAnalytics = {
	onFetchChannelsFailed: (params: PaginatedError & { teamId: string }) => void;
	onFetchChannelsSucceeded: (params: { channels: number; pages: number; teamId: string }) => void;
	onFetchTeamsFailed: (params: PaginatedError) => void;
	onFetchTeamsSucceeded: (params: { pages: number; teams: number }) => void;
	onFetchUsersFailed: (params: PaginatedError & { teamId: string }) => void;
	onFetchUsersSucceeded: (params: { pages: number; teamId: string; users: number }) => void;
	onGetEnabledFailed: (error: HttpError) => void;
	onGetEnabledSucceeded: () => void;
	onShareFailed: (
		params: HttpError & {
			conversationId: string;
			conversationType: SlackChannel['type'] | SlackUser['type'];
			product: AtlassianSlackProduct;
			teamId: string;
		},
	) => void;
	onShareSucceeded: (params: {
		conversationId: string;
		conversationType: SlackChannel['type'] | SlackUser['type'];
		messageLength: number;
		product: AtlassianSlackProduct;
		teamId: string;
		userCount: number;
	}) => void;
};

interface StreamDetails {
	count: number;
	finishedLoading: boolean;
	pages: number;
}

export default abstract class AbstractSlackService implements SlackService {
	private readonly baseUrl: string;
	// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
	private readonly windowName = uuid();
	protected abortController = typeof AbortController !== 'undefined' ? new AbortController() : null;
	private popupWindow?: WindowProxy;
	protected consentUrl?: string;
	private onSuccess?: () => void;

	protected userStream: StreamDetails = {
		count: 0,
		pages: 0,
		finishedLoading: false,
	};

	protected channelStream: StreamDetails = {
		count: 0,
		pages: 0,
		finishedLoading: false,
	};

	protected constructor(
		protected readonly cloudId: string,
		protected readonly product: AtlassianSlackProduct,
		protected readonly analytics: SlackServiceAnalytics,
	) {
		this.baseUrl = `${DEFAULT_SLACK_SERVICE_URL}/${product}`;
	}

	// Handle Slack authorization success message from popup window
	private onMessage = ({ data, target, source }: MessageEvent) => {
		if (!source || target !== window || data !== 'success') {
			return;
		}

		window.removeEventListener('message', this.onMessage);
		const popupWindow = source as WindowProxy;

		if (!this.popupWindow) {
			return;
		}

		popupWindow.close();
		this.popupWindow = undefined;

		this.onSuccess?.();
	};

	abort() {
		if (this.abortController) {
			this.abortController.abort();
			this.abortController = typeof AbortController !== 'undefined' ? new AbortController() : null;
		}
	}

	abstract primeConsentUrl(): Promise<void>;

	async getConsent(onSuccess: () => void) {
		await this.primeConsentUrl();

		this.onSuccess = onSuccess;
		window.addEventListener('message', this.onMessage);

		const popupWindow = window.open(
			this.consentUrl,
			this.windowName,
			popupWindowParams(POPUP_WIDTH, POPUP_HEIGHT),
		);

		if (popupWindow) {
			this.popupWindow = popupWindow;
		}
	}

	async isSlackEnabled(): Promise<IsSlackEnabledResult> {
		const result = await retryFetch<boolean>({
			request: () =>
				fetch(`${this.baseUrl}/enabled?cloudId=${this.cloudId}`, {
					signal: this.abortController?.signal,
				}),
			mapResult: (response) => response.json().then(({ enabled }) => enabled),
			mapError: (response) =>
				response.json().then((obj: { code?: string; message?: string }) => {
					const { code, message } = obj;

					return {
						code: code ?? '',
						message: message ?? '',
					};
				}),
		});
		if (result.ok) {
			this.analytics.onGetEnabledSucceeded();
		} else if (!result.aborted && !isAcceptableFailure(result.status)) {
			this.analytics.onGetEnabledFailed({
				status: result.status,
				errorMessage: result.message,
			});
		}

		return result;
	}

	async getTeams(): Promise<GetSlackTeamsResult> {
		const response = await fetchItemsWithCursor<SlackTeamsServiceResponse, SlackTeam>({
			url: `${this.baseUrl}/teams`,
			queryParams: { cloudId: this.cloudId },
			signal: this.abortController?.signal,
			getItems: (response) => response.teams,
			getCursor: (response) => response.nextCursor,
			canRetryCheck: httpRetryPolicy(retryOptions),
		});

		if (response.ok) {
			this.analytics.onFetchTeamsSucceeded({
				teams: response.items.length,
				pages: response.pages,
			});

			return {
				ok: true,
				result: sortBy(response.items, [({ name }) => name.toLowerCase()]),
			};
		} else if (response.aborted) {
			return response;
		} else {
			this.analytics.onFetchTeamsFailed({
				page: response.page,
				status: response.status,
				errorMessage: response.message,
			});

			return response;
		}
	}

	streamUsers(
		teamId: string,
		onResponse: (result: FetchSlackUsersResult) => void,
		cursor?: string,
	) {
		if (!cursor) {
			this.userStream = { count: 0, pages: 0, finishedLoading: false };
		}
		retryFetch<SlackUsersResponse>({
			request: () =>
				fetch(
					`${this.baseUrl}/recipients${buildQueryString({
						cloudId: this.cloudId,
						teamId,
						cursor,
						limit: USER_FETCH_LIMIT,
					})}`,
					{ signal: this.abortController?.signal },
				),
			canRetryCheck: httpRetryPolicy(retryOptions),
		}).then((resp) => {
			if (!resp.ok) {
				if (!resp.aborted) {
					this.analytics.onFetchUsersFailed({
						teamId,
						page: this.userStream.pages,
						status: resp.status,
						errorMessage: resp.message,
					});
				}
				onResponse(resp);
				return;
			}
			this.userStream.pages++;
			this.userStream.count += resp.result.recipients.length;
			this.userStream.finishedLoading = !!resp.result.nextCursor;
			onResponse({
				ok: true,
				result: resp.result.recipients,
				nextCursor: resp.result.nextCursor,
			});

			if (resp.result.nextCursor) {
				this.streamUsers(teamId, onResponse, resp.result.nextCursor);
			} else {
				// This event may not consistently fire because it is possible for the user to select and share before
				// all pages have loaded
				this.analytics.onFetchUsersSucceeded({
					teamId,
					users: this.userStream.count,
					pages: this.userStream.pages,
				});
			}
		});
	}

	streamChannels(
		teamId: string,
		onResponse: (result: GetSlackChannelsResult) => void,
		cursor?: string,
	) {
		if (!cursor) {
			this.channelStream = { count: 0, pages: 0, finishedLoading: false };
		}
		retryFetch<SlackChannelsResponse>({
			request: () =>
				fetch(
					`${this.baseUrl}/channels${buildQueryString({
						cloudId: this.cloudId,
						teamId,
						cursor,
					})}`,
					{ signal: this.abortController?.signal },
				),
			canRetryCheck: httpRetryPolicy(retryOptions),
		}).then((resp) => {
			if (!resp.ok) {
				if (!resp.aborted) {
					this.analytics.onFetchChannelsFailed({
						teamId,
						page: this.channelStream.pages,
						status: resp.status,
						errorMessage: resp.message,
					});
				}
				onResponse(resp);
				return;
			}
			this.channelStream.pages++;
			this.channelStream.count += resp.result.channels.length;
			this.channelStream.finishedLoading = !!resp.result.nextCursor;
			onResponse({
				ok: true,
				result: resp.result.channels,
				nextCursor: resp.result.nextCursor,
			});

			if (resp.result.nextCursor) {
				this.streamChannels(teamId, onResponse, resp.result.nextCursor);
			} else {
				// This event may not consistently fire because it is possible for the user to select and share before
				// all pages have loaded
				this.analytics.onFetchChannelsSucceeded({
					teamId,
					channels: this.channelStream.count,
					pages: this.channelStream.pages,
				});
			}
		});
	}

	async fetchSlackUsers(teamId: string): Promise<FetchSlackUsersResult> {
		const response = await fetchItemsWithCursor<SlackUsersResponse, SlackUser>({
			url: `${this.baseUrl}/recipients`,
			queryParams: { cloudId: this.cloudId, teamId },
			signal: this.abortController?.signal,
			getItems: (response) => response.recipients,
			getCursor: (response) => response.nextCursor,
			canRetryCheck: httpRetryPolicy(retryOptions),
		});
		if (response.ok) {
			const users = sortBy(response.items, [({ name }) => name.toLowerCase()]);
			this.analytics.onFetchUsersSucceeded({
				teamId,
				users: response.items.length,
				pages: response.pages,
			});
			return {
				ok: true,
				result: users,
			};
		} else if (response.aborted) {
			return response;
		} else {
			this.analytics.onFetchUsersFailed({
				teamId,
				page: response.page,
				status: response.status,
				errorMessage: response.message,
			});
			return response;
		}
	}

	async share({
		teamId,
		conversationId,
		conversationType,
		link,
		message,
	}: ShareToSlackRequest): Promise<ShareToSlackResult> {
		startUfoExperience(UfoExperiences.SLACK_REQUEST_SENT);

		const origin = new OriginTracer({ product: this.product });

		const response = await retryFetch<void>({
			request: () =>
				fetch(`${this.baseUrl}/share`, {
					method: 'post',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						cloudId: this.cloudId,
						teamId,
						conversation: {
							id: conversationId,
							type: conversationType,
						},
						link: origin.addToUrl(link),
						message,
					}),
					signal: this.abortController?.signal,
				}),
			mapResult: () => Promise.resolve(),
			mapError: (response) =>
				response.json().then((obj: { code?: string; message?: string }) => {
					const { code, message } = obj;
					return {
						code: code ?? '',
						message: message ?? '',
					};
				}),
			signal: this.abortController?.signal,
		});

		if (response.ok) {
			const successAttributes = {
				product: this.product,
				teamId,
				conversationType,
				conversationId,
				messageLength: message.length || 0,
				userCount: this.userStream.count,
			};
			this.analytics.onShareSucceeded(successAttributes);

			succeedUfoExperience(UfoExperiences.SLACK_REQUEST_SENT, successAttributes);
		} else if (!response.aborted && !isAcceptableFailure(response.status)) {
			const failedAttributes = {
				product: this.product,
				teamId,
				conversationType,
				conversationId,
				status: response.status,
				errorMessage: response.message,
			};
			this.analytics.onShareFailed(failedAttributes);

			failUfoExperience(UfoExperiences.SLACK_REQUEST_SENT, failedAttributes);
		}
		return response.ok
			? {
					ok: true,
					result: origin,
				}
			: response;
	}
}
